/**
 * Time zones for a select, named the way people say them ("Eastern Standard
 * Time") rather than the way the database stores them ("America/New_York").
 * The stored value stays the IANA id, which is what the server resolves.
 */

export interface TimezoneOption {
  id: string;
  label: string;
  offsetMinutes: number;
}

const COMMON = [
  "Pacific/Honolulu", "America/Anchorage", "America/Los_Angeles", "America/Denver", "America/Phoenix", "America/Chicago", "America/New_York",
  "America/Halifax", "America/Sao_Paulo", "Atlantic/Reykjavik", "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Athens", "Europe/Moscow",
  "Asia/Dubai", "Asia/Karachi", "Asia/Kolkata", "Asia/Dhaka", "Asia/Bangkok", "Asia/Shanghai", "Asia/Singapore", "Asia/Tokyo", "Asia/Seoul",
  "Australia/Perth", "Australia/Sydney", "Pacific/Auckland", "UTC",
];

function nameAt(id: string, date: Date): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: id, timeZoneName: "long" }).formatToParts(date);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? id;
  } catch {
    return id;
  }
}

function offsetAt(id: string, date: Date): number {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: id, timeZoneName: "longOffset" }).formatToParts(date);
    const raw = parts.find((p) => p.type === "timeZoneName")?.value ?? "GMT";
    const m = /GMT([+-])(\d{2}):?(\d{2})?/.exec(raw);
    if (!m) return 0;
    const sign = m[1] === "-" ? -1 : 1;
    return sign * (Number(m[2]) * 60 + Number(m[3] ?? 0));
  } catch {
    return 0;
  }
}

/** The zone's standard-time name: the January or July name, whichever is not daylight time. */
export function timezoneLabel(id: string, year = new Date().getFullYear()): string {
  const jan = nameAt(id, new Date(Date.UTC(year, 0, 1)));
  const jul = nameAt(id, new Date(Date.UTC(year, 6, 1)));
  const standard = [jan, jul].find((n) => !/daylight|summer/i.test(n)) ?? jan;
  const city = id.includes("/") ? id.split("/").pop()!.replace(/_/g, " ") : id;
  if (standard === id || /^GMT[+-]/.test(standard)) return `${city} (${standard})`;
  return standard === city ? standard : `${standard} · ${city}`;
}

function option(id: string): TimezoneOption {
  return { id, label: timezoneLabel(id), offsetMinutes: offsetAt(id, new Date(Date.UTC(new Date().getFullYear(), 0, 1))) };
}

function byOffset(a: TimezoneOption, b: TimezoneOption): number {
  return a.offsetMinutes - b.offsetMinutes || a.label.localeCompare(b.label);
}

export function formatOffset(minutes: number): string {
  const sign = minutes < 0 ? "−" : "+";
  const abs = Math.abs(minutes);
  return `UTC${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
}

/**
 * Two groups: the common zones most teams pick from, then everything the
 * browser knows. `current` is always present so a saved value never disappears.
 */
export function timezoneGroups(current?: string): Array<{ label: string; options: TimezoneOption[] }> {
  const all: string[] = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : COMMON;
  const commonSet = new Set(COMMON);
  if (current && !all.includes(current) && !commonSet.has(current)) all.push(current);
  const common = COMMON.filter((id) => all.includes(id) || id === "UTC").map(option).sort(byOffset);
  const rest = all.filter((id) => !commonSet.has(id)).map(option).sort(byOffset);
  if (current && !commonSet.has(current) && !rest.some((o) => o.id === current)) rest.push(option(current));
  return [
    { label: "Common", options: common },
    { label: "All time zones", options: rest },
  ];
}
